import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Briefcase, Plus, MapPin, Calendar, Receipt, FileCheck2, Clock, Download,
  CheckCircle2, PlayCircle, XCircle, CircleDashed,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

const STATUS = {
  scheduled: { label: "Geplant", cls: "bg-primary/15 text-primary", Icon: CircleDashed },
  in_progress: { label: "Läuft", cls: "bg-amber-500/15 text-amber-500", Icon: PlayCircle },
  done: { label: "Fertig", cls: "bg-accent/15 text-accent", Icon: CheckCircle2 },
  cancelled: { label: "Abgesagt", cls: "bg-destructive/15 text-destructive", Icon: XCircle },
};

const INV_STATUS = {
  paid: { label: "Bezahlt", cls: "bg-accent/15 text-accent" },
  open: { label: "Offen", cls: "bg-amber-500/15 text-amber-500" },
  overdue: { label: "Überfällig", cls: "bg-destructive/15 text-destructive" },
};

export default function CustomerPortal() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", address: "", scheduled_at: "" });

  async function load() {
    setLoading(true);
    try {
      const [j, i, r] = await Promise.all([
        api.get("/jobs"),
        api.get("/invoices").catch(() => ({ data: [] })),
        api.get("/reports").catch(() => ({ data: [] })),
      ]);
      setJobs(j.data || []);
      setInvoices(i.data || []);
      setReports(r.data || []);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function submitJob() {
    if (!form.title.trim()) { toast.error("Titel erforderlich"); return; }
    if (!form.address.trim()) { toast.error("Adresse erforderlich"); return; }
    try {
      await api.post("/jobs", {
        title: form.title.trim(),
        description: form.description,
        address: form.address.trim(),
        scheduled_at: form.scheduled_at || "",
        duration_minutes: 60,
        status: "scheduled",
        price: 0,
      });
      toast.success("Auftrag eingereicht — die Agentur meldet sich in Kürze");
      setOpen(false);
      setForm({ title: "", description: "", address: "", scheduled_at: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Absenden");
    }
  }

  function downloadInvoice(inv) {
    const text = `RECHNUNG ${inv.number}\n\nDatum: ${new Date(inv.created_at).toLocaleDateString('de-DE')}\n${inv.due_date ? `Fällig: ${inv.due_date}\n` : ""}\n\nPOSITIONEN:\n${inv.items.map(it => `- ${it.description}: ${it.qty} × € ${it.price.toFixed(2)} = € ${(it.qty * it.price).toFixed(2)}`).join("\n")}\n\nZwischensumme: € ${inv.subtotal.toFixed(2)}\nSteuer (${inv.tax_rate}%): € ${inv.tax.toFixed(2)}\nGESAMT: € ${inv.total.toFixed(2)}\n\nStatus: ${INV_STATUS[inv.status]?.label}`;
    const blob = new Blob([text], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${inv.number}.txt`;
    a.click();
  }

  const stats = useMemo(() => ({
    jobs_total: jobs.length,
    jobs_open: jobs.filter(j => j.status !== "done" && j.status !== "cancelled").length,
    reports: reports.length,
    invoices_open: invoices.filter(i => i.status === "open" || i.status === "overdue").length,
    invoices_open_sum: invoices.filter(i => i.status !== "paid").reduce((s, i) => s + (Number(i.total) || 0), 0),
  }), [jobs, invoices, reports]);

  if (loading) {
    return (
      <div className="py-20 flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-4 border-primary/30 border-t-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="customer-portal">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs text-muted-foreground font-bold uppercase tracking-[0.2em]">Willkommen zurück</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-2">Hallo, {user?.name?.split(" ")[0]}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Deine Aufträge, Berichte und Rechnungen an einem Ort
          </p>
        </div>
        <Button className="rounded-full h-11 px-6" onClick={() => setOpen(true)} data-testid="btn-new-request">
          <Plus className="w-4 h-4 mr-1.5" /> Neuer Auftrag
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Aufträge gesamt" value={stats.jobs_total} icon={Briefcase} />
        <StatCard label="Aktive Aufträge" value={stats.jobs_open} icon={Clock} accent />
        <StatCard label="Berichte" value={stats.reports} icon={FileCheck2} />
        <StatCard label="Offene Rechnungen" value={`€ ${stats.invoices_open_sum.toFixed(0)}`}
                  sub={`${stats.invoices_open} unbezahlt`} icon={Receipt} />
      </div>

      {/* Aufträge */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-xl font-semibold flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-primary" /> Deine Aufträge
          </h2>
          <span className="text-xs text-muted-foreground">{jobs.length} gesamt</span>
        </div>
        {jobs.length === 0 ? (
          <Card className="rounded-3xl p-10 text-center border-dashed border-border/60">
            <div className="text-sm text-muted-foreground mb-3">Noch keine Aufträge.</div>
            <Button className="rounded-full" onClick={() => setOpen(true)}>
              <Plus className="w-4 h-4 mr-1.5" /> Ersten Auftrag anlegen
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {jobs.map(j => {
              const st = STATUS[j.status] || STATUS.scheduled;
              return (
                <Card key={j.id} className="rounded-3xl p-5 border-border/60 glass" data-testid={`portal-job-${j.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="font-heading font-semibold truncate flex-1">{j.title}</div>
                    <Badge className={`rounded-full ${st.cls}`}>
                      <st.Icon className="w-3 h-3 mr-1" /> {st.label}
                    </Badge>
                  </div>
                  {j.address && (
                    <div className="text-xs text-muted-foreground flex items-center gap-1.5 mb-1">
                      <MapPin className="w-3.5 h-3.5" /> {j.address}
                    </div>
                  )}
                  {j.scheduled_at && (
                    <div className="text-xs text-muted-foreground flex items-center gap-1.5 mb-1">
                      <Calendar className="w-3.5 h-3.5" />
                      {new Date(j.scheduled_at).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })}
                    </div>
                  )}
                  {j.description && (
                    <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{j.description}</p>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </section>

      {/* Berichte */}
      {reports.length > 0 && (
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-xl font-semibold flex items-center gap-2">
              <FileCheck2 className="w-5 h-5 text-accent" /> Berichte
            </h2>
            <span className="text-xs text-muted-foreground">{reports.length}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {reports.map(r => {
              const job = jobs.find(j => j.id === r.job_id);
              return (
                <Card key={r.id} className="rounded-3xl p-5 border-border/60 glass" data-testid={`portal-report-${r.id}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-2xl bg-accent/15 text-accent flex items-center justify-center">
                      <FileCheck2 className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-heading font-semibold truncate">{job?.title || "Einsatz"}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {new Date(r.created_at).toLocaleDateString('de-DE')}
                      </div>
                    </div>
                  </div>
                  {r.notes && <p className="text-sm text-muted-foreground line-clamp-3 mb-2">{r.notes}</p>}
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span>{(r.photos || []).length} Foto(s)</span>
                    {r.signature && <span className="text-accent">✓ Signiert</span>}
                  </div>
                </Card>
              );
            })}
          </div>
        </section>
      )}

      {/* Rechnungen */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-xl font-semibold flex items-center gap-2">
            <Receipt className="w-5 h-5 text-primary" /> Rechnungen
          </h2>
          <span className="text-xs text-muted-foreground">{invoices.length}</span>
        </div>
        {invoices.length === 0 ? (
          <Card className="rounded-3xl p-8 text-center border-dashed border-border/60 text-sm text-muted-foreground">
            Noch keine Rechnungen.
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {invoices.map(inv => {
              const st = INV_STATUS[inv.status] || INV_STATUS.open;
              return (
                <Card key={inv.id} className="rounded-3xl p-5 border-border/60 glass flex items-center gap-4" data-testid={`portal-invoice-${inv.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="font-heading font-semibold truncate">{inv.number}</div>
                      <Badge className={`rounded-full text-[10px] ${st.cls}`}>{st.label}</Badge>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(inv.created_at).toLocaleDateString('de-DE')}
                      {inv.due_date && <> · fällig {inv.due_date}</>}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-heading text-xl font-bold">€ {Number(inv.total || 0).toFixed(2)}</div>
                    <Button size="sm" variant="ghost" className="rounded-full h-8 px-3 text-xs"
                            onClick={() => downloadInvoice(inv)}
                            data-testid={`portal-inv-download-${inv.id}`}>
                      <Download className="w-3.5 h-3.5 mr-1" /> Download
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </section>

      {/* New request dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-primary" /> Neuen Auftrag anfragen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Titel *</Label>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                     placeholder="z.B. Wartung Heizung"
                     className="rounded-full h-10 px-4 mt-1" data-testid="req-title" />
            </div>
            <div>
              <Label>Adresse *</Label>
              <Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })}
                     placeholder="Straße, Hausnummer, PLZ, Ort"
                     className="rounded-full h-10 px-4 mt-1" data-testid="req-address" />
            </div>
            <div>
              <Label>Wunschtermin</Label>
              <Input type="datetime-local" value={form.scheduled_at}
                     onChange={e => setForm({ ...form, scheduled_at: e.target.value })}
                     className="rounded-full h-10 px-4 mt-1" data-testid="req-date" />
            </div>
            <div>
              <Label>Beschreibung</Label>
              <Textarea value={form.description}
                        onChange={e => setForm({ ...form, description: e.target.value })}
                        placeholder="Was soll gemacht werden?"
                        rows={4}
                        className="rounded-2xl mt-1" data-testid="req-description" />
            </div>
            <p className="text-[11px] text-muted-foreground">
              Die Agentur bekommt deine Anfrage und meldet sich zeitnah mit einer Bestätigung und einem Angebot.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>
              Abbrechen
            </Button>
            <Button className="rounded-full" onClick={submitJob} data-testid="req-submit">
              Anfrage senden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, sub, icon: Icon, accent }) {
  return (
    <Card className={`rounded-3xl p-4 border-border/60 glass flex items-center gap-3 ${accent ? "bg-accent/5" : ""}`}>
      <div className={`w-11 h-11 rounded-2xl flex items-center justify-center ${
        accent ? "bg-accent/15 text-accent" : "bg-primary/15 text-primary"
      }`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <div className="font-heading text-2xl font-bold leading-tight truncate">{value}</div>
        <div className="text-[11px] text-muted-foreground uppercase tracking-wider">{label}</div>
        {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
      </div>
    </Card>
  );
}
